import { useEffect, useState, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '@/store/useAppStore'
import { ToastProvider } from '@/components/ui/Toast'
import { ReloadPrompt } from '@/components/ui/ReloadPrompt'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { DotFlow } from '@/components/ui/DotFlow'
import { AppShell } from '@/components/layout/AppShell'
import AuthPage from '@/pages/AuthPage'
import SignupPage from '@/pages/SignupPage'
import InvitePage from '@/pages/InvitePage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
// Lazy-loaded pages — auto-reload on chunk miss (stale deployment)
const lazyPage = (fn) => lazy(() => fn().catch(() => { window.location.reload(); return { default: () => null } }))
const ProjectPage        = lazyPage(() => import('@/pages/ProjectPage'))
const MyTasksPage        = lazyPage(() => import('@/pages/MyTasksPage'))
const InboxPage          = lazyPage(() => import('@/pages/InboxPage'))
const GoalsPage          = lazyPage(() => import('@/pages/GoalsPage'))
const PortfoliosPage     = lazyPage(() => import('@/pages/PortfoliosPage'))
const ReportingPage      = lazyPage(() => import('@/pages/ReportingPage'))
const ProfilePage        = lazyPage(() => import('@/pages/ProfilePage'))
const SetupBlanqPage     = lazyPage(() => import('@/pages/SetupBlanqPage'))
const HomeDashboardPage  = lazyPage(() => import('@/pages/HomeDashboardPage'))

// ── dot animation frames ──────────────────────────────────────────────────────
// Grid is 7×7, indices 0–48. Row r, col c → index r*7+c

// 1. Horizontal scan bar bouncing top↔bottom
const _scanline = [
  [0,1,2,3,4,5,6],
  [7,8,9,10,11,12,13],
  [14,15,16,17,18,19,20],
  [21,22,23,24,25,26,27],
  [28,29,30,31,32,33,34],
  [35,36,37,38,39,40,41],
  [42,43,44,45,46,47,48],
  [35,36,37,38,39,40,41],
  [28,29,30,31,32,33,34],
  [21,22,23,24,25,26,27],
  [14,15,16,17,18,19,20],
  [7,8,9,10,11,12,13],
]

// 2. Diagonal cascade — 3-wide beam sweeps top-left → bottom-right
const _cascade = [
  [0],
  [0,1,7],
  [0,1,2,7,8,14],
  [1,2,3,7,8,9,14,15,21],
  [2,3,4,8,9,10,14,15,16,21,22,28],
  [3,4,5,9,10,11,15,16,17,22,23,29,35],
  [4,5,6,10,11,12,16,17,18,23,24,30,36,42],
  [5,6,11,12,13,17,18,19,24,25,31,37,43],
  [6,12,13,18,19,20,25,26,32,38,44],
  [13,19,20,26,27,33,39,45],
  [20,26,27,33,34,40,46],
  [27,33,34,40,41,47],
  [34,40,41,46,47,48],
  [41,47,48],
  [48],
  [],
]

// 3. Concentric rings expanding from center then contracting
const _rings = [
  [],
  [24],
  [24,16,17,18,23,25,30,31,32],
  [24,16,17,18,23,25,30,31,32,8,9,10,11,12,15,19,22,26,29,33,36,37,38,39,40],
  [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39,40,41,42,43,44,45,46,47,48],
  [24,16,17,18,23,25,30,31,32,8,9,10,11,12,15,19,22,26,29,33,36,37,38,39,40],
  [24,16,17,18,23,25,30,31,32],
  [24],
  [],
]

// 4. Columns appear left→right then vanish right→left
const _columnwipe = [
  [0,7,14,21,28,35,42],
  [0,1,7,8,14,15,21,22,28,29,35,36,42,43],
  [0,1,2,7,8,9,14,15,16,21,22,23,28,29,30,35,36,37,42,43,44],
  [0,1,2,3,7,8,9,10,14,15,16,17,21,22,23,24,28,29,30,31,35,36,37,38,42,43,44,45],
  [0,1,2,3,4,7,8,9,10,11,14,15,16,17,18,21,22,23,24,25,28,29,30,31,32,35,36,37,38,39,42,43,44,45,46],
  [0,1,2,3,4,5,7,8,9,10,11,12,14,15,16,17,18,19,21,22,23,24,25,26,28,29,30,31,32,33,35,36,37,38,39,40,42,43,44,45,46,47],
  [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39,40,41,42,43,44,45,46,47,48],
  [1,2,3,4,5,6,8,9,10,11,12,13,15,16,17,18,19,20,22,23,24,25,26,27,29,30,31,32,33,34,36,37,38,39,40,41,43,44,45,46,47,48],
  [2,3,4,5,6,9,10,11,12,13,16,17,18,19,20,23,24,25,26,27,30,31,32,33,34,37,38,39,40,41,44,45,46,47,48],
  [3,4,5,6,10,11,12,13,17,18,19,20,24,25,26,27,31,32,33,34,38,39,40,41,45,46,47,48],
  [4,5,6,11,12,13,18,19,20,25,26,27,32,33,34,39,40,41,46,47,48],
  [5,6,12,13,19,20,26,27,33,34,40,41,47,48],
  [6,13,20,27,34,41,48],
  [],
]

// 5. Heartbeat
const _heartbit = [
  [],[3],[10,2,4,3],[17,9,1,11,5,10,4,3,2],
  [24,16,8,1,3,5,18,12,17,11,4,10,9,2],
  [31,23,15,8,10,2,4,12,25,19,24,18,11,17,16,9],
  [38,30,22,15,17,9,11,19,32,26,31,25,18,24,23,16],
  [38,30,22,17,9,11,19,32,26,31,25,18,24,23,16,45,37,29,21,14,8,15,12,20,27,33,39],
  [38,30,22,17,9,11,19,32,26,31,25,18,24,23,16,45,37,29,21,14,8,15,12,20,27,33,39],
  [38,30,22,15,17,9,11,19,32,26,31,25,18,24,23,16],
  [38,30,22,15,17,9,11,19,32,26,31,25,18,24,23,16],
  [38,30,22,17,9,11,19,32,26,31,25,18,24,23,16,45,37,29,21,14,8,15,12,20,27,33,39],
  [39,33,37,29,17,38,30,22,15,16,23,24,31,32,25,18,26,19],
  [17,30,16,23,24,31,32,25,18],[24],
]

const LOADING_ITEMS = [
  { title: 'Connecting',          frames: _scanline,    repeatCount: 2, duration: 120 },
  { title: 'Loading your tasks',  frames: _cascade,     repeatCount: 1, duration: 110 },
  { title: 'Syncing workspace',   frames: _rings,       repeatCount: 3, duration: 180 },
  { title: 'Building your board', frames: _columnwipe,  repeatCount: 1, duration: 100 },
  { title: 'Almost ready',        frames: _heartbit,    repeatCount: 1, duration: 110 },
]

// Polls /version.json every 2 min and on tab focus. When the SHA changes
// (new deploy is live), shows a full-screen blocking prompt that forces a reload
// — a stale bundle can desync against the new DB schema / API, so we don't let
// the user keep working on it.
function VersionPoller() {
  const [newVersion, setNewVersion] = useState(false)
  useEffect(() => {
    const current = import.meta.env.VITE_APP_VERSION || 'dev'
    if (import.meta.env.DEV || current === 'dev') return
    let shown = false
    const check = async () => {
      if (shown) return
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
        if (!res.ok) return
        const { sha } = await res.json()
        if (sha && sha !== current) {
          shown = true
          setNewVersion(true)
        }
      } catch { /* offline / fetch failed — try again next tick */ }
    }
    check()
    const id = setInterval(check, 2 * 60 * 1000)
    const onFocus = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [])
  return (
    <AnimatePresence>
      {newVersion && (
        <ReloadPrompt
          key="new-version"
          title="New version available"
          message="A new version of PRJCT is ready. Reload to get the latest — it only takes a second."
          buttonLabel="Reload now"
        />
      )}
    </AnimatePresence>
  )
}

function RequireAuth({ children }) {
  const isAuthenticated = useAppStore((s) => s.isAuthenticated)
  const authReady = useAppStore((s) => s._authReady)
  if (!authReady) return null  // loader is covering us — wait for session check
  if (!isAuthenticated) return <Navigate to="/auth" replace />
  return children
}

function DefaultRedirect() {
  return <Navigate to="/home" replace />
}

function LoadingScreen() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="fixed inset-0 bg-[#111111] flex items-center justify-center z-[9999]"
    >
      <DotFlow items={LOADING_ITEMS} />
    </motion.div>
  )
}

export default function App() {
  const initAuth = useAppStore((s) => s.initAuth)
  const authReady = useAppStore((s) => s._authReady)
  const accentColor = useAppStore((s) => s.accentColor)

  useEffect(() => {
    initAuth()
  }, [])

  useEffect(() => {
    if (accentColor) document.documentElement.style.setProperty('--color-accent', accentColor)
  }, [accentColor])

  return (
    <>
      <ToastProvider>
        <VersionPoller />
        <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<DefaultRedirect />} />
              <Route path="project/:projectId" element={<ErrorBoundary><Suspense fallback={null}><ProjectPage /></Suspense></ErrorBoundary>} />
              <Route path="home"       element={<ErrorBoundary><Suspense fallback={null}><HomeDashboardPage /></Suspense></ErrorBoundary>} />
              <Route path="my-tasks"  element={<ErrorBoundary><Suspense fallback={null}><MyTasksPage /></Suspense></ErrorBoundary>} />
              <Route path="schedule"  element={<Navigate to="/my-tasks" replace />} />
              <Route path="inbox"     element={<ErrorBoundary><Suspense fallback={null}><InboxPage /></Suspense></ErrorBoundary>} />
              <Route path="goals"     element={<ErrorBoundary><Suspense fallback={null}><GoalsPage /></Suspense></ErrorBoundary>} />
              <Route path="portfolios" element={<ErrorBoundary><Suspense fallback={null}><PortfoliosPage /></Suspense></ErrorBoundary>} />
              <Route path="reporting" element={<ErrorBoundary><Suspense fallback={null}><ReportingPage /></Suspense></ErrorBoundary>} />
              <Route path="profile"     element={<ErrorBoundary><Suspense fallback={null}><ProfilePage /></Suspense></ErrorBoundary>} />
              <Route path="setup-blanq" element={<ErrorBoundary><Suspense fallback={null}><SetupBlanqPage /></Suspense></ErrorBoundary>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
      <AnimatePresence>
        {!authReady && <LoadingScreen key="loading" />}
      </AnimatePresence>
    </>
  )
}
